'use client';

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { CartItem, CartSummary } from '../types/cart';

/**
 * Ventana (ms) durante la cual un item recién eliminado queda "protegido" de
 * reaparecer si un hydrate del host llega con datos aún no actualizados en el
 * servidor (carrera remove → getCart). Pasada la ventana, el guard expira para
 * no bloquear un re-alta legítima del mismo curso.
 */
const REMOVAL_GUARD_MS = 6000;

interface CartStore {
  items: CartItem[];
  /**
   * Totales del host; null cuando el sheet debe sumar los ítems por su cuenta.
   * No se persiste y cualquier cambio local de ítems lo invalida hasta el próximo hydrate.
   */
  summary: CartSummary | null;
  isOpen: boolean;
  /** id → timestamp de expiración del guard de eliminación optimista. */
  pendingRemovals: Record<string, number>;
  add: (item: CartItem) => void;
  hydrate: (items: CartItem[], summary?: CartSummary | null) => void;
  remove: (id: string) => void;
  clear: () => void;
  setIsOpen: (open: boolean) => void;
  has: (id: string) => boolean;
}

const pruneExpiredGuards = (
  guards: Record<string, number>,
  now: number,
): Record<string, number> => {
  const next: Record<string, number> = {};
  for (const [id, expiry] of Object.entries(guards)) {
    if (expiry > now) next[id] = expiry;
  }
  return next;
};

export const useCart = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      summary: null,
      isOpen: false,
      pendingRemovals: {},
      add: (item) =>
        set((state) => {
          // Un re-alta explícito cancela el guard de eliminación de ese curso.
          const { [item.id]: _removed, ...pendingRemovals } = state.pendingRemovals;
          if (state.items.some((i) => i.id === item.id)) return { pendingRemovals };
          return { items: [...state.items, item], summary: null, pendingRemovals };
        }),
      hydrate: (incoming, summary = null) =>
        set((state) => {
          const now = Date.now();
          const guards = pruneExpiredGuards(state.pendingRemovals, now);
          // Filtra los items que el usuario acaba de eliminar pero que el server
          // todavía devuelve (hydrate con datos stale).
          const items = incoming.filter((item) => !(item.id in guards));
          // Si el server ya dejó de devolver un item protegido, la baja se
          // confirmó: liberamos su guard.
          const incomingIds = new Set(incoming.map((item) => item.id));
          for (const id of Object.keys(guards)) {
            if (!incomingIds.has(id)) delete guards[id];
          }
          return { items, summary, pendingRemovals: guards };
        }),
      remove: (id) =>
        set((state) => ({
          items: state.items.filter((i) => i.id !== id),
          summary: null,
          pendingRemovals: {
            ...pruneExpiredGuards(state.pendingRemovals, Date.now()),
            [id]: Date.now() + REMOVAL_GUARD_MS,
          },
        })),
      clear: () => set({ items: [], summary: null, pendingRemovals: {} }),
      setIsOpen: (isOpen) => set({ isOpen }),
      has: (id) => get().items.some((i) => i.id === id),
    }),
    {
      name: 'adipa-cart',
      version: 1,
      partialize: (state) => ({ items: state.items }),
      migrate: (persistedState, version) => {
        if (version < 1) return { items: [] };
        return persistedState as { items: CartItem[] };
      },
    },
  ),
);
